import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { DealBase } from '@openx/types/deal';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DealRowActionType } from '../../constants';

const StyledContentTypography = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const StyledProceedMsgTypography = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const StyledAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

export type DealOperationPayload = Pick<DealBase, 'id' | 'name'>;

export interface DealsOperationDialogContentProps {
  content?: ReactNode;
  deals: DealOperationPayload[];
  action: DealRowActionType;
  isBulkMode: boolean;
  allDeals: number;
  isOa?: boolean;
  showRBOAlert?: boolean;
}

export function DealsOperationDialogContent({
  content,
  deals,
  action,
  isBulkMode,
  allDeals,
  isOa,
  showRBOAlert,
}: DealsOperationDialogContentProps) {
  const { t } = useTranslation();

  const actionText = action.toLowerCase();

  const getWarningText = useCallback(() => {
    let text = '';
    if (action === DealRowActionType.RESUME) {
      text = t(
        '{skiped} of the {all} selected Deal(s) will not be set to Active because they did not have Paused status or their deal type does not support this action.',
        {
          all: allDeals,
          skiped: allDeals - deals.length,
        }
      );
    } else if (action === DealRowActionType.PAUSE) {
      text = t(
        '{skiped} of the {all} selected Deal(s) will not be set to Paused because they did not have Active status or their deal type does not support this action.',
        {
          all: allDeals,
          skiped: allDeals - deals.length,
        }
      );
    } else if (action === DealRowActionType.DELETE && isOa) {
      text = t(
        '{skiped} of the {all} selected Deal(s) will not be deleted because they are in Publishing or Active status or their deal type does not support this action.',
        {
          all: allDeals,
          skiped: allDeals - deals.length,
        }
      );
    }

    return text;
  }, [action, allDeals, deals.length, t, isOa]);

  const dealText = isBulkMode ? t('deals') : t('deal');

  const alertTextType =
    action === DealRowActionType.RESUME
      ? 'Reactivating will cause this deal to run without Engagement optimization while request volumes are reassessed. '
      : 'Pausing will cause this deal to run without Engagement optimization while request volumes are reassessed. ';

  return (
    <DialogContent dividers>
      {deals.length !== allDeals && (
        <StyledAlert severity="error" data-test="alert-msg">
          {getWarningText()}
        </StyledAlert>
      )}
      {showRBOAlert && (
        <Alert severity="warning" sx={{ marginBottom: 2 }} data-test="rbo-alert">
          <strong>{t('CAUTION: ')}</strong>
          {t(alertTextType)}
          <strong>{t('Steady state performance may take up to 48 hours.')}</strong>
        </Alert>
      )}
      {!!deals.length && (
        <Typography variant="body1" data-test="intro-message">
          {t('You’re about to {actionText} the following {dealText}:', {
            actionText,
            dealText,
          })}
        </Typography>
      )}
      {deals.map(deal => (
        <StyledContentTypography variant="body1" key={deal.id} data-test="deal-name">
          {deal.name}
        </StyledContentTypography>
      ))}
      {content}
      {!!deals.length && (
        <StyledProceedMsgTypography variant="body1">{t('Do you want to proceed?')}</StyledProceedMsgTypography>
      )}
    </DialogContent>
  );
}
